import React, { useState } from 'react';
import { SxProps, AvatarGroup, Chip, Popover, Stack } from '@mui/material';
import { vars } from '../../../Theme';
import { parseUsername } from '../../../util/DataUtils';
import UserAvatar from '../Header/UserAvatar';
import CryptoJS from 'crypto-js';

/** Styles for the Chip Component. */
const chipSx: SxProps = {
    backgroundColor: vars.white,
};

/** Styles for the Avatar Group. */
const avatarGroupSx: SxProps = {
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-start',
};

/**
 * GroupedAvatar Component that returns avatars and names.
 */
export default ({ data }: IAvatarGroupProps) => {
    /** State slice for controlling the position of the Popover. */
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    /** Defines if the Popover is opened. */
    const open = Boolean(anchorEl);

    /** Sorts the data if it's an array. */
    const usersData = Array.isArray(data) ? [...data].sort() : data || [];

    /** On hover handler for the additional avatars. */
    const handlePopoverOpen = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    };

    /** On mouse leave handler for the Popover. */
    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    /** Function that returns Avatar + Username. */
    const renderChip = (users: string[] | string) => {
        const userList = Array.isArray(users) ? users : [users];
        return userList.map((x, i) => {
            const user = parseUsername(x); // name with company
            const name = user.split('-')[0].trim(); // name only

            return (
                <Chip
                    key={CryptoJS.MD5(x + i)}
                    sx={chipSx}
                    avatar={<UserAvatar size={25} userName={name} />}
                    label={user}
                />
            );
        });
    };

    return (
        <Stack>
            <AvatarGroup
                max={4}
                sx={avatarGroupSx}
                slotProps={{
                    additionalAvatar: {
                        onMouseEnter: handlePopoverOpen,
                    },
                }}
            >
                {renderChip(usersData)}
            </AvatarGroup>
            <Popover
                open={open}
                anchorEl={anchorEl}
                sx={{
                    '& .MuiPaper-root': {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    },
                }}
                PaperProps={{ onMouseLeave: handlePopoverClose }}
            >
                {renderChip(Array.isArray(usersData) ? usersData.slice(3) : usersData)}
            </Popover>
        </Stack>
    );
};

/**
 * Interface that describes the props for GroupedAvatar component.
 */
interface IAvatarGroupProps {
    /** User name text. */
    data: string[] | string;
}
